import { Form, Input, Tag } from "antd";
import { flow, JSON, pipe, Runtime } from "fp";
import React, { useState } from "react";
import styled from "styled-components";

import { Button } from "~/ButtonV2";
import * as Icon from "~/Icon";
import * as Log from "~/Log";
import { Remote } from "~/Remote";
import { Request } from "~/Request";
import { Time } from "~/Time";

export type PDFReportRequestProps = Pick<Log.Summaries.Identity, "interval"> &
  Partial<Pick<Log.Summaries.Identity, "driver">> & {
    readonly recipients: readonly string[];
  };

export const Container = styled.div`
  .ant-btn {
    margin-right: 8px;
  }
`;

export type Recipient = {
  readonly key: string;
  readonly email?: string;
};

export type EmailRecipientsFormProps = {
  readonly onClose: (recipients: readonly string[]) => void;
};

export function EmailRecipientsForm({ onClose }: EmailRecipientsFormProps) {
  const [formRef] = Form.useForm();
  const [recipients, setRecipients] = useState<readonly string[]>([]);
  const [emailPopulated, setEmailPopulated] = useState<boolean>(false);

  const addRecipient = (email: string) => {
    if (email && email.length > 0) {
      setRecipients([...recipients, email]);
      formRef.resetFields(["email"]);
    }
    setEmailPopulated(false);
  };

  const removeRecipient = (email: string) => {
    setRecipients(recipients.filter((r) => r !== email));
  };

  const onCancel = () => {
    formRef.resetFields(["email"]);
    onClose([]);
    setRecipients([]);
  };

  const onSubmit = () => {
    const emailRecipients = [...recipients];
    formRef.resetFields(["email"]);
    onClose(emailRecipients);
    setRecipients([]);
  };

  type Dictionary = {
    readonly [key: string]: string;
  };
  const onValuesChanged = (changedValues: Dictionary) => {
    setEmailPopulated(changedValues["email"].length > 0);
  };

  return (
    <Container>
      <Form
        form={formRef}
        {...{
          labelCol: {
            span: 10,
          },
          wrapperCol: {
            span: 12,
          },
        }}
        autoComplete="on"
        onValuesChange={onValuesChanged}
      >
        {recipients.length > 0 ? (
          <Form.Item name="recipients">
            <span style={{ display: "inline-block" }}>
              {recipients.map((r) => (
                <Tag key={r} closable onClose={() => removeRecipient(r)}>
                  {r}
                </Tag>
              ))}
            </span>
          </Form.Item>
        ) : null}

        <Form.Item label="Email address" name="email" shouldUpdate>
          <Input type="email" size="small" />
        </Form.Item>

        <Form.Item name="Additional">
          <Button
            type="link"
            icon={<Icon.Plus />}
            onClick={() => addRecipient(formRef.getFieldValue("email"))}
            disabled={!emailPopulated}
          >
            Add recipient
          </Button>
        </Form.Item>

        <Form.Item
          {...{
            wrapperCol: {
              offset: 10,
              span: 14,
            },
          }}
        >
          <Button onClick={() => onCancel()}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={recipients.length < 1}
            onClick={() => onSubmit()}
          >
            Send
          </Button>
        </Form.Item>
      </Form>
    </Container>
  );
}

export namespace LogReport {
  export const use = () =>
    Remote.Mutation.use<
      Query.LogReportResponse,
      readonly [PDFReportRequestProps]
    >(Query.request);

  export namespace Query {
    export const request = ({
      driver,
      interval,
      recipients,
    }: PDFReportRequestProps): Request.Fn<Query.LogReportResponse> => {
      const startDate = Time.DateTime.toISO(
        Time.DateTime.toStartOfDay(interval.start)
      );
      const endDate = Time.DateTime.toISO(
        Time.DateTime.toEndOfDay(interval.stop)
      );
      const endpoint = `/elogs/driver/${driver?.id}/logs-report/${startDate}/${endDate}`;

      return pipe(
        {
          endpoint,
          headers: { "Content-Type": "application/json" },
          body: JSON.Stringify.Always.short({
            addresses: recipients,
          }),
        },
        (config) =>
          flow(
            Request.fromConfig({ method: "POST", ...config }),
            Request.Expect.status(202, 422),
            Request.Expect.json(response()),
            Request.onSuccess(
              (response): LogReportResponse =>
                "message" in response
                  ? { error: response.message }
                  : { traceId: response.message_id }
            )
          )
      );
    };

    export const response = () =>
      Runtime.union([
        Runtime.strict({ message: Runtime.string() }),
        Runtime.strict({
          message_id: Runtime.string(),
        }),
      ]);

    export type LogReportResponse =
      | {
          readonly traceId: string;
        }
      | {
          readonly error: string;
        };
  }
}
